import React, { useState, useCallback, useEffect,useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {Paper,InputBase, CircularProgress,List,ListItem,Typography,Select,MenuItem,IconButton,Box,Switch,FormControlLabel} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import debounce from 'lodash.debounce';
import { getReleaseDetailsById, searchReleases } from '../../Services';
import EventDetails from '../Common/EventDetails/EventDetails';

const paperStyles = {
  p: '4px',
  display: 'flex',
  alignItems: 'center',
  width: '400px',
  backgroundColor: '#F3F3F3',
  position: 'relative',
  height: '35px',
  borderRadius: '4px', 
};

const selectStyles= {
  mr: 1,
  fontSize: '14px',
  color: '#000',
  height: '35px',
  backgroundColor: '#F3F3F3',
  '& .MuiSvgIcon-root': { color: '#333' },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' }
};

const menuProps= {
  PaperProps: {
    sx: {
      backgroundColor: '#F5F5F5',
      color: '#000',
      '& .MuiMenuItem-root': {
        fontSize: '14px',
        '&:hover': { backgroundColor: '#e0e0e0' },
        '&.Mui-selected': { backgroundColor: '#d0d0d0', color: '#333' },
        zIndex: 1200
      },
      maxWidth: '400px',
      overflowX: 'hidden'
    }
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  getContentAnchorEl: null
};

const inputBaseStyles= {
  ml: 1,
  flex: 1,
  fontSize: '14px',
  color: '#000'
};

const iconButtonStyles = {
  fontSize: '18px',
  color: '#000',
  '&:hover': { color: '#000' }
};

const resultsContainerStyles = {
  position: 'absolute',
  top: '66px',
  left: 0,
  width: '400px',
  background: '#F5F5F5',
  borderRadius: '0 0 8px 8px',
  overflowY: 'auto',
  maxHeight: '230px',
  zIndex: 2,
  whiteSpace: 'normal',
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 7px 8px -4px,-15px 0px 10px -17px rgba(0, 0, 0, 0.1), 15px 0px 10px -17px rgba(0, 0, 0, 0.1)',
};

const noResultsStyles = {
  position: 'absolute',
  top: '66px',
  left: 0,
  width: '400px',
  background: '#F5F5F5',
  borderRadius: '0 0 4px 4px',
  textAlign: 'center',
  padding: '10px',
  color: '#000',
  zIndex: 2,
  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 7px 8px -4px,-15px 0px 10px -17px rgba(0, 0, 0, 0.1), 15px 0px 10px -17px rgba(0, 0, 0, 0.1)',
  borderRadius: '0 0 8px 8px'
};

const listItemStyles = {
  padding: '8px',
  cursor: 'pointer',
  '&:hover': { background: '#e0e0e0' },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  textAlign: 'left'
};

const filterHeaderStyles = {
  width: '400px',
  background: '#f3f3f3',
  borderRadius: '8px 8px 0 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  px: 1,
  py: 0.3,
  zIndex: 2,
  position: 'absolute',
  top: '36px',
  left: 0,
  borderBottom: '2px solid #ddd'
};

const searchBarWrapperStyles = { position: 'relative' };
const SearchBar = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCategory, setSearchCategory] = useState('title');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pastReleases, setPastReleases] = useState(false);
  const [lastResults, setLastResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchContainerRef = useRef(null);
  const latestQueryRef = useRef('');


  const performSearch = useCallback(
    debounce(async (query) => {
      latestQueryRef.current = query;
      setIsLoading(true);
      try {
        const response = await searchReleases(query, searchCategory, pastReleases);
        if (latestQueryRef.current === query) {
          setResults(response.data.results || []);
          setLastResults(response.data.results || []);
          setShowResults(true);
        }
      } catch (error) {
        console.error('Error during search:', error);
        if (
          error?.response?.status === 401 ||
          error?.response?.data?.message === 'User authorization failed.'
        ) {
          setResults([]);
          navigate('/logout');
        } else {
          setResults([]);
          navigate('/error');
        }
      } finally {
        setIsLoading(false);
      }
    }, 400),
    [searchCategory, pastReleases]
  );

  useEffect(() => {
    if (searchTerm.length >= 3) {
      setIsLoading(true);
      performSearch(searchTerm);
    }
    return () => {
      performSearch.cancel();
    };
  }, [searchTerm, searchCategory, pastReleases]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && searchContainerRef.current.contains(event.target)) {
        return;
      }
      if (event.target.closest('.MuiMenu-root')) {
        return;
      }
      setShowResults(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  
  useEffect(() => {
    if (results.length > 0) {
      setLastResults(results);
    }
  }, [results]);
  

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 3) {
      setIsLoading(true);
      setShowResults(true);
      performSearch(value);
    } else {
      performSearch.cancel();
      setResults([]);
      setIsLoading(false);
      setShowResults(false);
    }
  };

  const handleCategoryChange = (e) => {
    setSearchCategory(e.target.value);
    setResults([]);
    setShowResults(true);
  };

  const handleClear = () => {
    setSearchTerm('');
    setResults([]);
    performSearch.cancel();
    setIsLoading(false);
  };

  const handleToggleFilter = () => {
    setPastReleases((prev) => !prev);
  };

  const handleResultClick = (id) => {
    fetchEventDetails(id);
  };

  const fetchEventDetails = async (releaseId) => {
    try {
      const eventDetails = await getReleaseDetailsById(releaseId);
      setSelectedEvent(eventDetails.data);
      setIsModalOpen(true);
    } catch (error) {
      if (
        error?.response?.status === 401 ||
        error?.response?.data?.message === 'User authorization failed.'
      ) {
        navigate('/logout');
      } else {
        navigate('/error');
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    if (searchTerm.length >= 3) {
      setShowResults(true);
    }
  
  };

  const handleInputFocus = () => {
    if (searchTerm.length >= 3) {
      setShowResults(true);
      if (results.length === 0 && lastResults.length > 0) {
        setResults([...lastResults]);
      }
    }
  };

  return (
    <div ref={searchContainerRef} className="search-bar-wrapper" style={searchBarWrapperStyles}>
      <Paper component="form" sx={paperStyles}>
        <Select
          value={searchCategory}
          onChange={handleCategoryChange}
          sx={selectStyles}
          MenuProps={menuProps}
        >
          <MenuItem value="id">ID</MenuItem>
          <MenuItem value="title">TITLE</MenuItem>
        </Select>
        <SearchIcon sx={{ fontSize: '18px', color: '#000', ml: 1 }} />
        <InputBase
          sx={inputBaseStyles}
          placeholder={`Search by ${searchCategory}`}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
        />
        {searchTerm.length > 0 && (
          <IconButton onClick={handleClear}>
            <ClearIcon sx={iconButtonStyles} />
          </IconButton>
        )}
      </Paper>

      {searchTerm.length >= 3 && showResults && (
        <Box sx={filterHeaderStyles}>
          <FormControlLabel
            control={
              <Switch
                checked={pastReleases}
                onChange={(e) => setPastReleases(e.target.checked)}
                color="primary"
                size="small"
                sx={{
                  transform: "scale(0.85)",
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#333', 
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#444', 
                  },
                  '& .MuiSwitch-switchBase': {
                    color: '#fff',
                  },
                  '& .MuiSwitch-switchBase + .MuiSwitch-track': {
                    backgroundColor: '#a0a0a0',
                  },
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: 9, textTransform: 'uppercase', ml: 0.2 }}>
                Past Releases
              </Typography>
            }
            sx={{ m: 0, padding: 0,color:"#000" }}
          />
        </Box>
      )}

      {searchTerm.length >= 3 && showResults && (
        <>
          {isLoading ? (
            <div
              style={{
                position: 'absolute',
                top: '66px',
                left: 0,
                width: '400px',
                background: '#F5F5F5',
                borderRadius: '0 0 8px 8px',
                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 7px 8px -4px,-15px 0px 10px -17px rgba(0, 0, 0, 0.1), 15px 0px 10px -17px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
                padding: '10px',
                color: '#000',
                zIndex: 2
              }}
            >
              <CircularProgress size={20} sx={{ color: '#333' }} />
            </div>
          ) : results.length > 0 ? (
            <div style={resultsContainerStyles}>
              <List>
                {results.map((result) => (
                  <ListItem
                    key={result.id}
                    onClick={() => handleResultClick(result.id)}
                    sx={listItemStyles}
                  >
                    <Typography sx={{ color: '#000', fontSize: '13px', mb: 0.5 }}>
                      {result.title}
                    </Typography>
                    <Typography sx={{ color: '#555', fontSize: '12px' }}>
                      {result.id} &nbsp; | &nbsp; {result.service} &nbsp; | &nbsp; {result.category}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </div>
          ) : (
            <div style={noResultsStyles}>No results found.</div>
          )}
        </>
      )}

      {selectedEvent && (
        <EventDetails event={selectedEvent} isOpen={isModalOpen} setIsModalOpen={closeModal} />
      )}
    </div>
  );
};

export default SearchBar;